/**
 * The scene DIRECTOR — Phase 2: a PARAMETRIC director with CONSTRAINT FRAMING and an OVERHEAD backbone.
 *
 * The owner's #1 note was "por cima, pega os dois, entende o plano todo" — the clip should READ. So the spine of
 * every cut is now an OVERHEAD, constraint-solved beat that keeps BOTH combatants inside the exported 9:16 frame
 * (`framesolver.solveTwoShip`), and the low/dramatic shots (the muzzle, the bullet-time, a ram) are EARNED SPICE
 * layered on top — 1–2 punches, never the base. The narrative spirit of v56 survives intact: the killing
 * cannonball is still the protagonist (bullet-time on the TRUE fatal ball), the naufrágio is still filmed to the
 * END, and the clip still fades out.
 *
 * The cut is built from a feature-driven BUDGET, not a fixed arc:
 *   1. a readability FLOOR — an overhead ESTABLISH opener + a solver-framed SINK close, both framing the two
 *      ships (this is the guaranteed spine, so `bothShipsInFramePct` can never collapse the way it did in v56);
 *   2. earned DRAMA — a low muzzle beat, a ball TRACK, the BULLET-TIME climax, or a ram — selected by the scene
 *      features (distance, kind, hero ball) and the STYLE preset's drama budget;
 *   3. STYLE presets (Cinematic / Hype / Tactical) = weight vectors over the SAME knobs; the Cinematic defaults
 *      were AUTO-TUNED against the real-kill corpus (see tools/killcam-bench/src/autotune.ts).
 *
 * PURE of THREE / DOM / renderer — the framing math is in framesolver.ts, the camera path in directorcamera.ts.
 * Unit-tested in shotdirector.test.ts.
 */
import { EventType, REPLAY, BULLET_TIME, SINK_FX } from '@piratas/protocol';
import { mulberry32 } from '@piratas/sim';
import type { FrozenReplay, RepEvent } from './replaybuffer.js';
import { SUN_AZIMUTH } from './framesolver.js';

export type BeatKind = 'establish' | 'chase' | 'orbit' | 'broadside' | 'muzzle' | 'track' | 'cannonball' | 'impact' | 'ram' | 'sink' | 'aerial';
export type Mood = 'epic' | 'storm' | 'night' | 'golden';

export interface Beat {
  kind: BeatKind;
  realStart: number;   // seconds into the cut this beat begins
  realDur: number;     // seconds it lasts
  warp: 'linear' | 'bullet'; // how real-time maps to sim-time within the beat
  scrubStart: number;  // sim time shown at realStart (linear beats)
  scrubEnd: number;    // sim time shown at realEnd (linear beats)
  speedScale: number;  // informational slow-mo hint (the ACTUAL slow-mo = simSpan/realDur, set per beat below)
  focusId: number;     // the primary entity this beat frames (ship OR ball — the "hero" of the beat)
  targetId: number;    // the secondary entity (the victim / the other party)
  ballId: number;      // the hero cannonball (track / cannonball beats), else -1
  side: number;        // broadside side (0/1) for the muzzle/broadside beat, else -1
  orbitDir: number;    // +1 / -1 whip/orbit spin direction (seed-varied)
  framing: number;     // 0 = hug close, 1 = wide far — the seed-varied camera distance for track/sink
  // bullet-time anchors (warp === 'bullet')
  scrubFreeze: number; // sim time held frozen during the whip-orbit
  scrubImpact: number; // sim time the release snaps forward to (the burst)
  aimX: number; aimY: number; // world aim point of the fatal blow (impact FX / framing)
  // ── overhead-solver beats (kind === 'aerial' | 'sink') — the constraint-framing spine ──
  heightFrac: number;  // camera-height ÷ stand-off → the fixed downward pitch of the overhead (bigger = more top-down)
  marginFrac: number;  // keep both ships this fraction clear of the frame edges
  lookBias: number;    // 0 = look at the centroid of the two ships, 1 = look at the focus ship (sink emphasis)
  preferredYaw: number;// STABLE camera bearing (fixed once from the fatal-moment axis + sun) — the overhead never flips side
}

export interface Cut {
  beats: Beat[];
  totalReal: number;
  mood: Mood;
  music: string;       // asset base name under public/audio/replay/ (no extension)
  grade: GradeParams;  // colour grade applied over every frame of the clip
  seed: number;
  heroId: number;
  killT: number;
  fatal: FatalBlow;
  byBullet: boolean;   // a cannon kill with a real hero ball AND a style that keeps the bullet-time climax
  sinkStart: number;   // sim time the victim starts going under (the player spawns the wreck cinematic here)
  sinkEnd: number;     // sim time the wreck cinematic has fully played out (drives scrub past the tape tail)
  title: string;       // short EN caption for the export card
  style: StyleName;    // the style preset this cut was composed under
}

export interface FatalBlow { victimId: number; killerId: number; x: number; y: number; t: number; ballId: number; }
export interface GradeParams { saturation: number; brightness: number; warm: number; vignette: number; tint: [number, number, number]; }

/* ─────────────────────────────── style presets + tunable knobs ─────────────────────────────── */

export type StyleName = 'cinematic' | 'hype' | 'tactical';

/** The director's tunable knobs. The Cinematic defaults are AUTO-TUNED against the corpus; the other presets are
 *  weight vectors over these same knobs. Every field is explainable (the "gosto manual" half lives here). */
export interface DirectorKnobs {
  spineHeightFrac: number;  // overhead-ness (pitch) of the ESTABLISH/aftermath aerial spine beats
  sinkHeightFrac: number;   // overhead-ness of the solver-framed SINK close
  aerialMargin: number;     // frame margin the solver keeps both ships clear of the edges
  openerDurMul: number;     // length of the overhead opener (× a base shot)
  sinkDurMul: number;       // length of the sink close (× SHOT_MAX)
  trackDurMul: number;      // length of the ball TRACK spice (× a base shot); 0 → drop the track beat
  muzzleDurMul: number;     // length of the low MUZZLE spice (× a base shot)
  bulletTime: number;       // 1 = keep the bullet-time climax; 0 = replace it with an overhead impact aerial
  aftermathAerial: number;  // 1 = insert a second overhead beat after the climax (more overhead / legibility)
  dramaBudget: number;      // max number of LOW dramatic spice beats (muzzle/track/chase/orbit) allowed
  heightJitter: number;     // seed jitter on the overhead pitch for clip-to-clip variety
  sinkLookBias: number;     // how hard the sink look leans onto the victim (0 = centroid, 1 = the wreck)
}

/** Cinematic — the DEFAULT. More overhead, longer, keeps the bullet-time signature + an aftermath aerial.
 *  These numbers are the AUTO-TUNED output of `pnpm --filter @piratas/killcam-bench run autotune`. */
export const DEFAULT_KNOBS: DirectorKnobs = {
  // structural knobs = the AUTO-TUNED output (autotune.ts over the 22-kill corpus, under the HARD "both combatants on
  // every overhead" constraint): a high-3/4 overhead (pitch ~53°) whose FOV opens as needed to always hold both.
  spineHeightFrac: 1.35, sinkHeightFrac: 1.2,
  openerDurMul: 1.9, sinkDurMul: 1.8, trackDurMul: 0, muzzleDurMul: 0.7,
  bulletTime: 1, aftermathAerial: 1, dramaBudget: 2, sinkLookBias: 0.2,
  // "gosto" overrides (kept manual on purpose): a touch more frame margin than the point-proxy search wants (real
  // ship MESHES have extent the metric's deck-point ignores) + some clip-to-clip height variety ("alterna longe/perto").
  aerialMargin: 0.10, heightJitter: 0.24,
};

/** The three selectable looks — each a partial override of the Cinematic defaults (same engine, different weights). */
export const STYLE_PRESETS: Record<StyleName, Partial<DirectorKnobs>> = {
  // Cinematic = the tuned defaults (more overhead, slower, bullet-time + aftermath).
  cinematic: {},
  // Hype = punchier, LOWER angle, faster cuts, more spice, keeps the bullet-time; the overhead still frames both.
  hype: { spineHeightFrac: 1.0, sinkHeightFrac: 1.0, openerDurMul: 1.05, sinkDurMul: 1.15, trackDurMul: 1.0, muzzleDurMul: 1.1, aftermathAerial: 0, dramaBudget: 3, heightJitter: 0.35 },
  // Tactical = almost everything from above (more top-down), drama minimal (NO bullet-time — an overhead impact instead).
  tactical: { spineHeightFrac: 2.1, sinkHeightFrac: 2.1, openerDurMul: 1.9, sinkDurMul: 1.7, trackDurMul: 0, muzzleDurMul: 0.7, bulletTime: 0, aftermathAerial: 1, dramaBudget: 1, heightJitter: 0.18 },
};

/** Resolve the effective knob set for a style, with an optional explicit override (used by the auto-search). */
export function resolveKnobs(style: StyleName, override?: Partial<DirectorKnobs>): DirectorKnobs {
  return { ...DEFAULT_KNOBS, ...STYLE_PRESETS[style], ...(override ?? {}) };
}

/** The module-level style used when `composeCut` is called without an explicit one (prod default = Cinematic).
 *  `__cc.killcamStyle('hype'|'tactical'|'cinematic')` flips it at runtime; the bench passes styles explicitly. */
let CURRENT_STYLE: StyleName = 'cinematic';
export function setKillcamStyle(s: StyleName): void { CURRENT_STYLE = s; }
export function getKillcamStyle(): StyleName { return CURRENT_STYLE; }

export interface ComposeOpts { style?: StyleName; knobs?: Partial<DirectorKnobs>; }

const MUSIC_EPIC = 'highlight_epic';
const MUSIC_STORM = 'highlight_storm';
/** The full client-side sink cinematic length (DAMAGE + SINK phases) — the director films the sink to THIS end. */
const SINK_FILM_S = SINK_FX.DAMAGE_S + SINK_FX.SINK_S;

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }

/** Colour grade per mood — the "look" the compositor multiplies over each exported frame. */
function gradeFor(mood: Mood): GradeParams {
  switch (mood) {
    case 'storm':  return { saturation: 0.72, brightness: 0.94, warm: -0.06, vignette: 0.42, tint: [0.80, 0.86, 1.0] };  // desaturated, cold, dramatic
    case 'night':  return { saturation: 1.02, brightness: 1.04, warm: 0.02, vignette: 0.36, tint: [0.86, 0.92, 1.12] }; // moonlit, lanterns pop
    case 'golden': return { saturation: 1.12, brightness: 1.06, warm: 0.16, vignette: 0.28, tint: [1.12, 1.02, 0.82] }; // warm golden-hour
    default:       return { saturation: 1.08, brightness: 1.02, warm: 0.06, vignette: 0.32, tint: [1.04, 1.0, 0.96] };  // epic neutral-punchy
  }
}

/** Pick the mood from the environment at the kill moment. Storm and night are strong signals; otherwise the sea
 *  state (wind/wave) splits calm golden-hour from an adrenaline "epic". */
export function moodFor(night: number, storm: number, windStrength: number): Mood {
  if (storm > 0.35) return 'storm';
  if (night > 0.5) return 'night';
  if (windStrength > 1.25) return 'epic';
  return 'golden';
}

/** The fatal SINK closest to the trigger time. Carries the FATAL BALL ID (event `c`, v56). Falls back to a
 *  flood/self sink if none was captured. */
function fatalBlow(frozen: FrozenReplay, heroId: number): FatalBlow {
  let best: RepEvent | null = null; let bestD = Infinity;
  for (const e of frozen.events) {
    if (e.kind !== EventType.SINK) continue;
    const d = Math.abs(e.t - frozen.killT);
    if (d < bestD) { bestD = d; best = e; }
  }
  if (best) return { victimId: best.a, killerId: best.b, x: best.x, y: best.y, t: best.t, ballId: best.c | 0 };
  return { victimId: heroId, killerId: 0, x: 0, y: 0, t: frozen.killT, ballId: 0 }; // flood / unknown
}

/** Was there a ram impact near the fatal moment (a ram kill)? Returns the RAM event or null. */
function ramNear(frozen: FrozenReplay, t: number): RepEvent | null {
  let best: RepEvent | null = null; let bestD = 1.4;
  for (const e of frozen.events) { if (e.kind !== EventType.RAM) continue; const d = Math.abs(e.t - t); if (d < bestD) { bestD = d; best = e; } }
  return best;
}

/** The killing HIT on the victim near the fatal moment (carries the true impact point — the FALLBACK aim point). */
function fatalHit(frozen: FrozenReplay, fatal: FatalBlow): RepEvent | null {
  let best: RepEvent | null = null; let bestD = 1.6;
  for (const e of frozen.events) {
    if (e.kind !== EventType.HIT) continue;
    if (fatal.victimId && e.b !== fatal.victimId) continue; // b = victimId
    const d = Math.abs(e.t - fatal.t);
    if (d <= bestD) { bestD = d; best = e; }
  }
  return best;
}

export interface HeroBall { ballId: number; launchT: number; impactT: number; launchX: number; launchY: number; x: number; y: number; }

/**
 * THE RELIABLE PATH. Reconstruct the killing ball's WHOLE trajectory from the buffer by its id (the server-stamped
 * fatal ball id). A cannonball only exists in the buffer between its launch and its impact, so the FIRST frame that
 * carries the id is the muzzle-launch and the LAST is the impact — no correlation, no guessing, no ambiguity even
 * with a dozen balls in flight at point-blank range. Returns null if the id is 0 or never appeared on the tape.
 */
export function heroBallById(frozen: FrozenReplay, ballId: number): HeroBall | null {
  if (!ballId) return null;
  let launchT = Infinity, impactT = -Infinity, lx = 0, ly = 0, ix = 0, iy = 0, seen = false;
  for (const fr of frozen.frames) {
    for (const b of fr.balls) {
      if (b.id !== ballId) continue;
      seen = true;
      if (fr.t < launchT) { launchT = fr.t; lx = b.x; ly = b.y; }
      if (fr.t > impactT) { impactT = fr.t; ix = b.x; iy = b.y; }
    }
  }
  if (!seen) return null;
  return { ballId, launchT, impactT, launchX: lx, launchY: ly, x: ix, y: iy };
}

/** LEGACY FALLBACK ONLY. The hero cannonball guessed as the one whose captured trajectory passes CLOSEST to the
 *  fatal impact point around the fatal moment. Used only when the server sent no fatal-ball id (pre-v56). */
export function pickHeroBall(frozen: FrozenReplay, aimX: number, aimY: number, aimT: number): { ballId: number; impactT: number; x: number; y: number } | null {
  const T0 = aimT - 1.6, T1 = aimT + 0.35;
  let bestId = -1; let bestDist = 26; let bestT = aimT; let bestX = aimX; let bestY = aimY; // 26 m gate
  for (const fr of frozen.frames) {
    if (fr.t < T0 || fr.t > T1) continue;
    for (const b of fr.balls) {
      const d = Math.hypot(b.x - aimX, b.y - aimY);
      if (d < bestDist) { bestDist = d; bestId = b.id; bestT = fr.t; bestX = b.x; bestY = b.y; }
    }
  }
  if (bestId < 0) return null;
  let impT = bestT; let impD = Infinity;
  for (const fr of frozen.frames) {
    if (fr.t < T0 || fr.t > T1) continue;
    for (const b of fr.balls) if (b.id === bestId) { const d = Math.hypot(b.x - aimX, b.y - aimY); if (d < impD) { impD = d; impT = fr.t; } }
  }
  return { ballId: bestId, impactT: impT, x: bestX, y: bestY };
}

/** The killer's broadside FIRE nearest to (and preferably just before) the ball's launch — frames the MUZZLE beat. */
function fireForLaunch(frozen: FrozenReplay, shooterId: number, launchT: number): RepEvent | null {
  let before: RepEvent | null = null, any: RepEvent | null = null, anyD = Infinity;
  for (const e of frozen.events) {
    if (e.kind !== EventType.FIRE || e.a !== shooterId) continue;
    const d = Math.abs(e.t - launchT);
    if (d < anyD) { anyD = d; any = e; }
    if (e.t <= launchT + 0.05 && (!before || e.t > before.t)) before = e;
  }
  return before ?? any;
}

/** How many distinct ships sank inside the window (a multi-kill earns a wider, more triumphant opener). */
function sinkCount(frozen: FrozenReplay): number {
  const s = new Set<number>();
  for (const e of frozen.events) if (e.kind === EventType.SINK) s.add(e.a);
  return s.size;
}

/** A ship's (x,y) at the frame nearest sim time `t` (self + ships), or null if never present. */
function shipPosNear(frozen: FrozenReplay, id: number, t: number): { x: number; y: number } | null {
  if (!id) return null;
  let best: { x: number; y: number } | null = null, bestD = Infinity;
  for (const fr of frozen.frames) {
    const d = Math.abs(fr.t - t);
    if (d >= bestD) continue;
    if (fr.self && fr.self.id === id) { best = { x: fr.self.x, y: fr.self.y }; bestD = d; continue; }
    for (const s of fr.ships) if (s.id === id) { best = { x: s.x, y: s.y }; bestD = d; break; }
  }
  return best;
}

/** Engagement separation of killer↔victim at the fatal moment (m), or -1 if a party is off-tape. */
function engageDistOf(frozen: FrozenReplay, fatal: FatalBlow): number {
  const kp = shipPosNear(frozen, fatal.killerId, fatal.t), vp = shipPosNear(frozen, fatal.victimId, fatal.t) ?? { x: fatal.x, y: fatal.y };
  return kp ? Math.hypot(kp.x - vp.x, kp.y - vp.y) : -1;
}

/** Angular distance between two bearings, in [0, π]. */
function angDelta(a: number, b: number): number { let d = Math.abs(a - b) % (Math.PI * 2); if (d > Math.PI) d = Math.PI * 2 - d; return d; }

/** The STABLE overhead camera bearing for a kill: stand ALONG the killer↔victim axis (ships stack in depth → the
 *  tall 9:16 axis), on the SUN side (front-lit, no silhouette). Fixed ONCE here so the solver never flips side. */
function preferredYawFor(frozen: FrozenReplay, fatal: FatalBlow, fallback: number): number {
  const kp = shipPosNear(frozen, fatal.killerId, fatal.t);
  const vp = shipPosNear(frozen, fatal.victimId, fatal.t) ?? { x: fatal.x, y: fatal.y };
  if (!kp || (kp.x === vp.x && kp.y === vp.y)) return fallback;
  const axis = Math.atan2(vp.y - kp.y, vp.x - kp.x);
  // camera bearing = dir from centroid → camera; pick the axis end nearest the sun so the sun sits behind the camera
  return angDelta(axis, SUN_AZIMUTH) <= angDelta(axis + Math.PI, SUN_AZIMUTH) ? axis : axis + Math.PI;
}

const BULLET_REAL = BULLET_TIME.APPROACH_S + BULLET_TIME.RAMP_IN_S + BULLET_TIME.ORBIT_S + BULLET_TIME.HOLD_S + BULLET_TIME.RELEASE_S;

/**
 * Compose the cut. `heroId` = the LOCAL captain's ship id (framed prominently whether they killed or died).
 * Deterministic given the same frozen window + heroId + style/knobs (seeded off the kill).
 */
export function composeCut(frozen: FrozenReplay, heroId: number, opts: ComposeOpts = {}): Cut {
  const style = opts.style ?? CURRENT_STYLE;
  const knobs = resolveKnobs(style, opts.knobs);

  const fatal = fatalBlow(frozen, heroId);
  const seed = ((Math.round(frozen.killT * 1000) & 0x7fffffff) ^ (fatal.victimId * 2654435761) ^ (fatal.killerId * 40503) ^ (fatal.ballId * 2246822519)) >>> 0;
  const rng = mulberry32(seed || 1);

  // ---- environment / mood at the kill moment ----
  let night = 0, storm = 0, wind = 1;
  { let bestD = Infinity; for (const fr of frozen.frames) { const d = Math.abs(fr.t - fatal.t); if (d < bestD) { bestD = d; night = fr.env.night; storm = fr.env.storm; wind = fr.env.windStrength; } } }
  const mood = moodFor(night, storm, wind);
  const music = mood === 'storm' ? MUSIC_STORM : MUSIC_EPIC;
  const grade = gradeFor(mood);
  const multi = sinkCount(frozen) >= 2;

  // ---- hero ball: the TRUE killing shot (from its id), or a legacy heuristic fallback ----
  let ballId = fatal.ballId;
  if (!ballId && fatal.killerId) {
    const hit = fatalHit(frozen, fatal);
    const guess = pickHeroBall(frozen, hit ? hit.x : fatal.x, hit ? hit.y : fatal.y, hit ? hit.t : fatal.t);
    if (guess) ballId = guess.ballId;
  }
  const heroBall = heroBallById(frozen, ballId);
  const ram = ramNear(frozen, fatal.t);
  const hasBall = !!heroBall;
  const byBullet = hasBall && knobs.bulletTime > 0; // Tactical drops the bullet-time climax for an overhead impact
  const engageDist = engageDistOf(frozen, fatal);

  const t0 = frozen.t0, t1 = frozen.t1;
  const orbitDir = rng() < 0.5 ? 1 : -1;
  const framing = rng();                 // near/far for this clip (owner: alternate long/close between clips)
  const jitter = (rng() * 2 - 1) * knobs.heightJitter; // per-clip overhead-pitch jitter (variety)
  const victimId = fatal.victimId, killerId = fatal.killerId || heroId;
  const preferredYaw = preferredYawFor(frozen, fatal, framing * Math.PI * 2); // STABLE overhead bearing (never flips)

  const beats: Beat[] = [];
  const mk = (b: Partial<Beat> & { kind: BeatKind }): Beat => ({
    realStart: 0, realDur: REPLAY.SHOT_MIN_S, warp: 'linear', scrubStart: t0, scrubEnd: t1, speedScale: 1,
    focusId: heroId, targetId: victimId, ballId: -1, side: -1, orbitDir, framing, scrubFreeze: 0, scrubImpact: 0,
    aimX: fatal.x, aimY: fatal.y, heightFrac: knobs.spineHeightFrac + jitter, marginFrac: knobs.aerialMargin, lookBias: 0, preferredYaw, ...b,
  });
  const shotDur = (): number => REPLAY.SHOT_MIN_S + rng() * (REPLAY.SHOT_MAX_S - REPLAY.SHOT_MIN_S);

  // The sink always caps the arc: the victim goes under and we film it to the END.
  const sinkStart = clamp(fatal.t, t0 + 0.1, t1);
  const sinkEnd = sinkStart + SINK_FILM_S;

  // the SOLVER-FRAMED SINK close — the readability FLOOR's back half: overhead, films the wreck to the END while
  // KEEPING BOTH COMBATANTS in frame (owner mandate — the solver opens the FOV if the killer drifted far). The look
  // leans onto the wreck (focus = victim) but the killer stays framed (target = killer) whenever it is on tape.
  const sinkOther = fatal.killerId || victimId;   // ALWAYS both combatants (single-ship only for a flood — no killer)
  const sinkBeat = (): Beat => mk({
    kind: 'sink', realDur: REPLAY.SHOT_MAX_S * knobs.sinkDurMul, scrubStart: sinkStart, scrubEnd: sinkEnd, speedScale: 0.85,
    focusId: victimId, targetId: sinkOther, aimX: fatal.x, aimY: fatal.y,
    heightFrac: knobs.sinkHeightFrac + jitter, lookBias: knobs.sinkLookBias,
  });

  let drama = 0; // low/dramatic spice beats spent (the "1–2 socos" budget)

  if (hasBall && heroBall) {
    // ═══ CANNON KILL → overhead ESTABLISH (spine) → [muzzle] → [track] → BULLET-TIME | overhead impact → [aftermath aerial] → SINK ═══
    const impactT = clamp(heroBall.impactT, t0 + 0.3, t1);
    const launchT = clamp(heroBall.launchT, t0, impactT - 0.15);
    const scrubImpact = impactT;
    const scrubFreeze = clamp(scrubImpact - 0.16, launchT + 0.05, scrubImpact - 0.02);
    const approachStart = clamp(scrubFreeze - 0.12, launchT + 0.05, scrubFreeze - 0.03);
    const fire = fireForLaunch(frozen, killerId, launchT);
    const muzzleFrom = clamp((fire ? fire.t : launchT) - 0.45, t0, launchT);

    // 0 — SPINE OPENER: overhead establish framing BOTH ships as they close (the readability floor's front half).
    beats.push(mk({ kind: 'aerial', realDur: shotDur() * knobs.openerDurMul, focusId: killerId, targetId: victimId,
      scrubStart: t0, scrubEnd: clamp(muzzleFrom - 0.2, t0 + 0.05, muzzleFrom) }));

    // 1 — MUZZLE (low, spice + FX): the killer's guns firing. Kept whenever a fire was found and the budget allows.
    const wantMuzzle = knobs.muzzleDurMul > 0 && drama < knobs.dramaBudget;
    if (wantMuzzle) {
      drama++;
      beats.push(mk({ kind: 'muzzle', realDur: shotDur() * knobs.muzzleDurMul, scrubStart: muzzleFrom, scrubEnd: clamp(launchT + 0.05, muzzleFrom + 0.02, approachStart), speedScale: 0.3, focusId: killerId, targetId: victimId, ballId: heroBall.ballId, side: fire ? fire.b | 0 : -1, aimX: fire ? fire.x : heroBall.launchX, aimY: fire ? fire.y : heroBall.launchY }));
    }

    // 2 — TRACK (low, spice): follow the killing ball. The search dropped it for close/mid (a low beat hurts
    // legibility), but a LONG-RANGE SNIPE is exactly where watching the hero ball cross the sea earns its keep — so
    // keep it for long shots (also gives long kills a distinct cut signature). Dropped for a point-blank stub.
    const trackMul = knobs.trackDurMul > 0 ? knobs.trackDurMul : (engageDist > 160 ? 0.8 : 0);
    const wantTrack = trackMul > 0 && drama < knobs.dramaBudget && (engageDist < 0 || engageDist > 18);
    if (wantTrack) {
      drama++;
      beats.push(mk({ kind: 'track', realDur: shotDur() * trackMul, scrubStart: clamp(launchT + 0.05, muzzleFrom + 0.03, approachStart), scrubEnd: approachStart, speedScale: 0.5, focusId: killerId, targetId: victimId, ballId: heroBall.ballId, aimX: heroBall.x, aimY: heroBall.y }));
    }

    if (byBullet) {
      // 3 — CANNONBALL (bullet-time): the ball striking the hull — freeze, whip-orbit, snap to the burst.
      beats.push(mk({ kind: 'cannonball', warp: 'bullet', realDur: BULLET_REAL, ballId: heroBall.ballId, focusId: heroId, targetId: victimId, scrubStart: approachStart, scrubEnd: scrubImpact, scrubFreeze, scrubImpact, aimX: heroBall.x, aimY: heroBall.y }));
    } else {
      // 3' — OVERHEAD IMPACT (Tactical): frame the hit from above, both ships in shot (drama minimal).
      beats.push(mk({ kind: 'aerial', realDur: shotDur() * 1.1, focusId: killerId, targetId: victimId, scrubStart: approachStart, scrubEnd: clamp(scrubImpact + 0.2, approachStart + 0.05, sinkStart), heightFrac: knobs.spineHeightFrac + jitter, marginFrac: Math.max(0.05, knobs.aerialMargin - 0.03), aimX: heroBall.x, aimY: heroBall.y }));
    }

    // 4 — AFTERMATH AERIAL (spine, optional): a second overhead of the moment of the kill before the sink. It cuts
    // in from the TIGHT bullet-time, so it REVEALS (pulls open onto both ships) rather than whipping.
    if (knobs.aftermathAerial > 0 && byBullet) {
      beats.push(mk({ kind: 'aerial', realDur: shotDur() * 0.9, focusId: killerId, targetId: victimId,
        scrubStart: clamp(scrubImpact, t0, sinkStart), scrubEnd: sinkStart, heightFrac: knobs.spineHeightFrac + jitter }));
    }

    // 5 — SINK close (spine).
    beats.push(sinkBeat());
  } else if (ram) {
    // ═══ RAM KILL → overhead ESTABLISH (spine) → chase (spice) → RAM (slow-mo climax) → SINK (spine) ═══
    const ramT = clamp(ram.t, t0 + 0.3, t1 - 0.1);
    beats.push(mk({ kind: 'aerial', realDur: shotDur() * knobs.openerDurMul, focusId: killerId, targetId: victimId, scrubStart: t0, scrubEnd: clamp(ramT - 1.6, t0 + 0.05, ramT - 0.5) }));
    if (drama < knobs.dramaBudget) { drama++; beats.push(mk({ kind: 'chase', realDur: shotDur(), scrubStart: clamp(ramT - 1.6, t0, ramT - 0.6), scrubEnd: clamp(ramT - 0.5, t0 + 0.05, ramT - 0.1), focusId: killerId, targetId: victimId })); }
    beats.push(mk({ kind: 'ram', realDur: REPLAY.SHOT_MAX_S * 1.1, scrubStart: clamp(ramT - 0.5, t0, ramT - 0.05), scrubEnd: clamp(ramT + 0.35, ramT, t1), speedScale: REPLAY.SLOWMO_SCALE, aimX: ram.x, aimY: ram.y, focusId: killerId, targetId: victimId }));
    beats.push(sinkBeat());
  } else {
    // ═══ NO HERO BALL → overhead ESTABLISH (spine) → orbit (spice) → SINK (spine) ═══
    // This also catches a CANNON kill whose fatal ball rolled off the tape (slow flood): there IS an attacker, so the
    // overhead MUST frame BOTH combatants (owner mandate). Only a TRUE flood (no killer) frames the wreck alone.
    const other = fatal.killerId || victimId;
    beats.push(mk({ kind: 'aerial', realDur: shotDur() * knobs.openerDurMul, focusId: victimId, targetId: other, scrubStart: clamp(sinkStart - 4.0, t0, sinkStart - 1.2), scrubEnd: clamp(sinkStart - 1.0, t0 + 0.05, sinkStart - 0.2) }));
    if (drama < knobs.dramaBudget) { drama++; beats.push(mk({ kind: 'orbit', realDur: shotDur(), scrubStart: clamp(sinkStart - 1.0, t0, sinkStart - 0.2), scrubEnd: sinkStart, focusId: victimId, targetId: victimId })); }
    beats.push(sinkBeat());
  }

  // optional extra wide overhead opener on a multi-kill (a triumphant establish — more overhead, still ends on sink).
  // SPLIT the opener's own scrub window so the two aerials stay CONTIGUOUS in sim-time (a non-contiguous prepend
  // would rewind the scrub at the beat boundary → a camera teleport through the crossfade).
  if (multi && beats.length && beats[0]!.kind === 'aerial') {
    const op = beats[0]!;
    const midScrub = (op.scrubStart + op.scrubEnd) * 0.5;
    const wide = mk({ kind: 'aerial', realDur: shotDur(), focusId: op.focusId, targetId: op.targetId,
      scrubStart: op.scrubStart, scrubEnd: midScrub, heightFrac: op.heightFrac + 0.25, marginFrac: op.marginFrac + 0.03 });
    op.scrubStart = midScrub;   // the original opener now covers the SECOND half → contiguous with `wide`
    beats.unshift(wide);
  }

  const overheadKind = (k: BeatKind): boolean => k === 'aerial' || k === 'sink';

  // ---- SCRUB CONTIGUITY into the sink: on a SLOW-FLOOD kill the fatal ball lands long before the hull finally goes
  // under, so an overhead framing the IMPACT would scrub-jump 10 s+ to the SINK between two frames (a teleport). Make
  // the overhead immediately before the sink scrub CONTINUOUSLY up to sinkStart instead (it fast-forwards the flood).
  for (let i = 1; i < beats.length; i++) {
    if (beats[i]!.kind === 'sink' && overheadKind(beats[i - 1]!.kind) && beats[i - 1]!.scrubEnd < beats[i]!.scrubStart) {
      beats[i - 1]!.scrubEnd = beats[i]!.scrubStart;
    }
  }

  // ---- clamp the total real length into the shareable [TOTAL_MIN, TOTAL_MAX] window ----
  // The bullet-time climax has a FIXED real duration; fit by scaling only the LINEAR beats proportionally so the
  // whole cut lands in the sweet spot without touching the money shot.
  const bulletReal = beats.reduce((s, b) => s + (b.warp === 'bullet' ? b.realDur : 0), 0);
  const linearBeats = beats.filter((b) => b.warp === 'linear');
  let linearSum = linearBeats.reduce((s, b) => s + b.realDur, 0);
  const maxLinear = Math.max(REPLAY.SHOT_MIN_S, REPLAY.TOTAL_MAX_S - bulletReal);
  const minLinear = Math.max(REPLAY.SHOT_MIN_S, REPLAY.TOTAL_MIN_S - bulletReal);
  if (linearSum > maxLinear && linearSum > 0) { const f = maxLinear / linearSum; for (const b of linearBeats) b.realDur *= f; linearSum = maxLinear; }
  if (linearSum < minLinear && linearSum > 0) { const f = minLinear / linearSum; for (const b of linearBeats) b.realDur *= f; }

  // ---- re-stamp sequential realStart ----
  let acc = 0; for (const b of beats) { b.realStart = acc; acc += b.realDur; }

  const title = titleFor(fatal, heroId, hasBall, !!ram, multi);
  return { beats, totalReal: acc, mood, music, grade, seed, heroId, killT: frozen.killT, fatal, byBullet, sinkStart, sinkEnd, title, style };
}

function titleFor(fatal: FatalBlow, heroId: number, byBullet: boolean, byRam: boolean, multi: boolean): string {
  const iKilled = fatal.killerId === heroId && fatal.victimId !== heroId;
  if (multi && iKilled) return 'MULTI-KILL';
  if (iKilled) return byBullet ? 'BULLSEYE' : byRam ? 'RAMMED' : 'SUNK';
  if (fatal.victimId === heroId) return 'WENT DOWN FIGHTING';
  return 'SHIP SUNK';
}

// Keep a stable reference so tree-shakers don't drop the sun constant re-export path used by the camera solver.
export { SUN_AZIMUTH };
